/**
 * Client-side image optimizer
 * Resizes to max 2560px, converts to WebP, quality 85%
 * Returns optimized File + compression stats
 */

export interface OptimizationResult {
  file: File;
  originalSize: number;
  optimizedSize: number;
  savedPercent: number;
  width: number;
  height: number;
}

const MAX_DIMENSION = 2560;
const QUALITY = 0.85;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatOptimizationStats(result: OptimizationResult): string {
  return `${formatBytes(result.originalSize)} → ${formatBytes(result.optimizedSize)} (${result.savedPercent}% menor)`;
}

export async function optimizeImage(file: File): Promise<OptimizationResult> {
  const originalSize = file.size;

  // Skip non-image files or SVGs (vector, no need to compress)
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return { file, originalSize, optimizedSize: originalSize, savedPercent: 0, width: 0, height: 0 };
  }

  // Skip GIFs (animated, can't compress with canvas)
  if (file.type === 'image/gif') {
    return { file, originalSize, optimizedSize: originalSize, savedPercent: 0, width: 0, height: 0 };
  }

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Scale down if exceeds max dimension
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve({ file, originalSize, optimizedSize: originalSize, savedPercent: 0, width, height });
        return;
      }

      // Use high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Try WebP first, fallback to JPEG
      const tryFormat = (format: string, quality: number): Promise<Blob | null> => {
        return new Promise((res) => {
          canvas.toBlob(
            (blob) => res(blob),
            format,
            quality,
          );
        });
      };

      (async () => {
        let blob = await tryFormat('image/webp', QUALITY);
        let ext = 'webp';

        // If WebP not supported or blob is null, fallback to JPEG
        if (!blob || blob.size === 0) {
          blob = await tryFormat('image/jpeg', QUALITY);
          ext = 'jpg';
        }

        if (!blob) {
          resolve({ file, originalSize, optimizedSize: originalSize, savedPercent: 0, width, height });
          return;
        }

        // Only use optimized version if it's actually smaller
        if (blob.size >= originalSize) {
          resolve({ file, originalSize, optimizedSize: originalSize, savedPercent: 0, width, height });
          return;
        }

        const baseName = file.name.replace(/\.[^.]+$/, '');
        const optimizedFile = new File([blob], `${baseName}.${ext}`, { type: blob.type });

        const savedPercent = Math.round((1 - blob.size / originalSize) * 100);

        resolve({
          file: optimizedFile,
          originalSize,
          optimizedSize: blob.size,
          savedPercent,
          width,
          height,
        });
      })();
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ file, originalSize, optimizedSize: originalSize, savedPercent: 0, width: 0, height: 0 });
    };

    img.src = url;
  });
}

/**
 * Optimize multiple files in parallel
 */
export async function optimizeImages(files: File[]): Promise<OptimizationResult[]> {
  return Promise.all(files.map(optimizeImage));
}
